// Conflict resolution for the sync layer. Pure functions, no I/O — so
// the dangerous bits (OR-merge with the uncheck exception) get reasoned
// about and tested in isolation. Per plan/02 §Write strategy.

import type { DailyProgress, Id } from '../../lib/types';

interface TimestampedRecord {
  id: Id;
  updatedAt: string;
}

// Per-record last-writer-wins for tree records (profiles/routines/tasks).
// Compare the two ISO timestamps; remote wins on equality so the family
// converges on what the server saw last (closer to canonical).
export function mergeTreeRecord<T extends TimestampedRecord>(local: T | null, remote: T): T {
  if (local === null) return remote;
  // ISO 8601 sorts lexicographically.
  if (remote.updatedAt >= local.updatedAt) return remote;
  return local;
}

// Pure OR-merge for the per-day completed booleans. A slot is true if either
// side is true — no "uncheck exception" at the sync layer. This is intentional:
// cross-device concurrent taps must not silently drop any completion. Single-
// device deselect is handled in the hook layer via per-slot in-memory timestamps
// (see useRoutineProgress). Cross-device deselect propagation requires a schema
// bump to add per-slot timestamps to the wire format (deferred follow-up).
export function mergeProgress(local: DailyProgress | null, remote: DailyProgress): DailyProgress {
  if (local === null) {
    return {
      ...remote,
      routines: { ...remote.routines },
    };
  }

  // Union routine ids from both sides — a routine present on only one
  // side carries through verbatim.
  const allRoutineIds = new Set<Id>([
    ...Object.keys(local.routines),
    ...Object.keys(remote.routines),
  ]);

  const merged: Record<Id, boolean[]> = {};
  for (const id of allRoutineIds) {
    const a = local.routines[id];
    const b = remote.routines[id];
    if (a === undefined) {
      merged[id] = (b ?? []).slice();
      continue;
    }
    if (b === undefined) {
      merged[id] = a.slice();
      continue;
    }
    merged[id] = mergeCompletedArrays(a, b);
  }

  return {
    profileId: local.profileId,
    dateKey: local.dateKey,
    routines: merged,
    updatedAt: local.updatedAt > remote.updatedAt ? local.updatedAt : remote.updatedAt,
  };
}

// Pure OR-merge, padding to the longer length. Exposed for the engine
// which sometimes wants a per-routine merge without rebuilding a
// DailyProgress wrapper.
export function mergeCompletedArrays(local: boolean[], remote: boolean[]): boolean[] {
  const len = Math.max(local.length, remote.length);
  const out: boolean[] = new Array<boolean>(len).fill(false);
  for (let i = 0; i < len; i++) {
    out[i] = local[i] === true || remote[i] === true;
  }
  return out;
}
